import React, { useState, useEffect, useCallback } from 'react';
import { FaIdCard, FaUser, FaUsers, FaBirthdayCake, FaBuilding, FaBriefcase, FaSpinner, FaCalendarAlt, FaUpload, FaExclamationTriangle, FaDesktop, FaNetworkWired, FaEnvelope, FaFingerprint, FaCalculator, FaLaptop, FaMobileAlt, FaTabletAlt, FaPaperclip, FaBell } from 'react-icons/fa';
import { Modal } from './common';
import VacantePicker from './VacantePicker';
import axiosInstance from '../utils/axiosConfig';
import { ESTADOS_VIGENTES, rutaPuesto, iniciales } from './vacantesShared';
import './NewEmployeeModal.css';
import './FormModal.css';
import './VacantesUI.css';

// Orden en que se ofrecen los candidatos: los mas avanzados primero
const ORDEN_ETAPA = { 'Seleccionado': 0, 'Entrevistado': 1, 'En revisión': 2, 'Nuevo': 3 };

const capitalizar = (texto) => (texto || '')
  .toLowerCase()
  .split(' ')
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  .join(' ');

// "Ana María Pérez Gómez" -> nombres "Ana María", apellidos "Pérez Gómez".
// Con tres palabras se asume un nombre y dos apellidos, lo mas comun aqui.
// Es solo un punto de partida: los campos quedan editables.
const separarNombre = (completo) => {
  const p = capitalizar((completo || '').trim()).split(/\s+/).filter(Boolean);
  if (p.length <= 3) return { nombres: p[0] || '', apellidos: p.slice(1).join(' ') };
  return { nombres: p.slice(0, 2).join(' '), apellidos: p.slice(2).join(' ') };
};

/**
 * Registrar ingreso: todo empleado nuevo ocupa una vacante.
 *
 * Paso 1 elige la vacante (o la abre si el puesto no existe todavia).
 * Paso 2 pide solo los datos de la persona: el puesto (gerencia, departamento,
 * area, cargo y supervisor) sale de la vacante y no se puede cambiar aqui.
 *
 * Con `initialVacanteId` (desde la vacante o desde un candidato) arranca en el
 * paso 2 con el puesto fijo.
 *
 * Si la vacante tiene candidatos activos, hay que decir si ingresa uno de ellos
 * (sus datos se precargan y queda Contratado) u otra persona.
 */
function NewEmployeeModal({ onClose, onEmployeeAdded, initialVacanteId = '', initialCandidateData = null }) {
  const puestoFijo = !!initialVacanteId;

  const [paso, setPaso] = useState(initialVacanteId ? 2 : 1);
  const [vacante, setVacante] = useState(null);
  const [showAbrirVacante, setShowAbrirVacante] = useState(false);
  // Candidatos activos de la vacante elegida y quien ingresa: id de candidato,
  // 'otro' o null mientras no se decide
  const [candidatos, setCandidatos] = useState([]);
  const [quienIngresa, setQuienIngresa] = useState(initialCandidateData ? initialCandidateData.id : null);

  const [formData, setFormData] = useState({
    nombres: '',
    apellidos: '',
    ficha: '',
    cedula: '',
    correo: '',
    sede_id: '',
    detalles_cargo: '',
    fecha_nacimiento: '',
    fecha_ingreso: '',
  });

  const [asignaciones, setAsignaciones] = useState({
    usuario_red: false,
    correo_electronico: false,
    huella_acceso: false,
    usuarios_profit: false,
    profit_administrativo: false,
    profit_contable: false,
    profit_nomina: false,
    centro_sura: false,
    cima: false,
    soluciones_ti: false
  });

  const [hardwareRequerido, setHardwareRequerido] = useState({
    tipo_equipo: '', // 'laptop', 'aio', o 'ninguno'
    telefono: false,
    tablet: false
  });

  const [sedes, setSedes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFilesRif, setSelectedFilesRif] = useState([]);
  const [selectedFilesCedula, setSelectedFilesCedula] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [enviarCorreo, setEnviarCorreo] = useState(true);

  useEffect(() => {
    axiosInstance.get('/api/sedes')
      .then((response) => setSedes(response.data))
      .catch((err) => {
        console.error('Error al cargar sedes:', err);
        setError('Error al cargar sedes');
      });
  }, []);

  useEffect(() => {
    if (initialVacanteId) {
      axiosInstance.get(`/api/vacantes/${initialVacanteId}`)
        .then((response) => {
          if (!ESTADOS_VIGENTES.includes(response.data.estado)) {
            setError(`La vacante está ${response.data.estado.toLowerCase()} y no admite ingresos`);
          }
          setVacante(response.data);
        })
        .catch(() => setError('No se pudo cargar la vacante'));
    }
  }, [initialVacanteId]);

  const precargarCandidato = useCallback((candidato) => {
    const { nombres, apellidos } = separarNombre(candidato?.nombre);
    setFormData((prev) => ({
      ...prev,
      nombres,
      apellidos,
      cedula: (candidato?.cedula || '').replace(/\D/g, ''),
    }));
  }, []);

  // Datos que vienen del candidato elegido en el pipeline
  useEffect(() => {
    if (!initialCandidateData) return;
    precargarCandidato(initialCandidateData);
    setAsignaciones((prev) => ({ ...prev, correo_electronico: true }));
  }, [initialCandidateData, precargarCandidato]);

  // Al fijar la vacante, traer sus candidatos activos (salvo que ya venga uno elegido)
  useEffect(() => {
    setCandidatos([]);
    if (!vacante || initialCandidateData || !vacante.candidatos_activos_count) return;
    axiosInstance.get(`/api/vacantes/${vacante.id}/candidatos`)
      .then((response) => {
        const activos = response.data
          .filter((c) => c.estado in ORDEN_ETAPA)
          .sort((a, b) => ORDEN_ETAPA[a.estado] - ORDEN_ETAPA[b.estado]);
        setCandidatos(activos);
        // Un unico seleccionado es casi seguro quien ingresa
        const seleccionados = activos.filter((c) => c.estado === 'Seleccionado');
        if (seleccionados.length === 1) {
          setQuienIngresa(seleccionados[0].id);
          precargarCandidato(seleccionados[0]);
        }
      })
      .catch(() => setCandidatos([])); // sin permiso de candidatos: ingreso normal
  }, [vacante, initialCandidateData, precargarCandidato]);

  const elegirQuien = (valor) => {
    setQuienIngresa(valor);
    setError(null);
    if (valor === 'otro') {
      setFormData((prev) => ({ ...prev, nombres: '', apellidos: '', cedula: '' }));
    } else {
      precargarCandidato(candidatos.find((c) => c.id === valor));
    }
  };

  const elegirVacante = (v) => {
    if (v.id !== vacante?.id) {
      setQuienIngresa(null);
      setFormData((prev) => ({ ...prev, nombres: '', apellidos: '', cedula: '' }));
    }
    setVacante(v);
    setError(null);
    setPaso(2);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'cedula' || name === 'ficha') {
      setFormData((prev) => ({ ...prev, [name]: value.replace(/\D/g, '') }));
    } else if (name === 'nombres' || name === 'apellidos') {
      const formattedValue = capitalizar(value);

      setFormData((prev) => {
        const newData = { ...prev, [name]: formattedValue };
        // Generar correo automáticamente si ambos campos están llenos y correo_electronico está marcado
        if (newData.nombres && newData.apellidos && asignaciones.correo_electronico) {
          const primerNombre = newData.nombres.split(' ')[0].toLowerCase();
          const primerApellido = newData.apellidos.split(' ')[0].toLowerCase();
          newData.correo = `${primerNombre}.${primerApellido}`;
        }
        return newData;
      });
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const subirAdjunto = async (empleadoId, file, tipo) => {
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    uploadFormData.append('ficha', formData.ficha);
    uploadFormData.append('tipo', tipo);
    await axiosInstance.post(`/api/empleados/${empleadoId}/adjuntos`, uploadFormData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!vacante) {
      setError('Elige la vacante que cubre este ingreso');
      return;
    }
    if (candidatos.length > 0 && !quienIngresa) {
      setError('Indica si ingresa uno de los candidatos de la vacante u otra persona');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      // Separar nombres y apellidos en sus partes
      const nombresArr = formData.nombres.trim().split(/\s+/);
      const apellidosArr = formData.apellidos.trim().split(/\s+/);

      const empleadoData = {
        surte_vacante_id: vacante.id,
        candidato_id: quienIngresa && quienIngresa !== 'otro' ? quienIngresa : null,
        nombre: nombresArr[0] || '',
        segundo_nombre: nombresArr.slice(1).join(' ') || null,
        apellido: apellidosArr[0] || '',
        segundo_apellido: apellidosArr.slice(1).join(' ') || null,
        ficha: formData.ficha,
        cedula: formData.cedula,
        correo: formData.correo,
        sede_id: formData.sede_id,
        detalles_cargo: formData.detalles_cargo,
        fecha_nacimiento: formData.fecha_nacimiento,
        fecha_ingreso: formData.fecha_ingreso,
        // Hardware
        necesita_aio: hardwareRequerido.tipo_equipo === 'aio' ? 1 : 0,
        necesita_laptop: hardwareRequerido.tipo_equipo === 'laptop' ? 1 : 0,
        necesita_telefono: hardwareRequerido.telefono ? 1 : 0,
        necesita_tablet: hardwareRequerido.tablet ? 1 : 0,
        // Software y accesos
        necesita_usuario_red: asignaciones.usuario_red ? 1 : 0,
        necesita_correo: asignaciones.correo_electronico ? 1 : 0,
        necesita_huella: asignaciones.huella_acceso ? 1 : 0,
        // Módulos de Profit
        necesita_profit_adm: asignaciones.profit_administrativo ? 1 : 0,
        necesita_profit_com: asignaciones.profit_contable ? 1 : 0,
        necesita_profit_nom: asignaciones.profit_nomina ? 1 : 0,
        // Cuentas corporativas adicionales
        necesita_centro_sura: asignaciones.centro_sura ? 1 : 0,
        necesita_cima: asignaciones.cima ? 1 : 0,
        necesita_soluciones_ti: asignaciones.soluciones_ti ? 1 : 0,
        // Control de notificación por correo
        enviar_correo: enviarCorreo,
      };

      const response = await axiosInstance.post('/api/empleados', empleadoData);

      if (response.data && response.data.empleado) {
        const empleadoId = response.data.empleado.id;
        if (selectedFilesRif.length + selectedFilesCedula.length > 0) {
          setUploadingFiles(true);
          for (const file of selectedFilesRif) await subirAdjunto(empleadoId, file, 'rif');
          for (const file of selectedFilesCedula) await subirAdjunto(empleadoId, file, 'cedula');
          setUploadingFiles(false);
        }

        onEmployeeAdded(response.data.empleado);
        onClose();
      } else {
        console.error('Respuesta del servidor incompleta:', response.data);
        setError('La respuesta del servidor no incluye los datos del empleado');
      }
    } catch (err) {
      console.error('Error al registrar ingreso:', err);
      if (err.response) {
        setError(err.response.data.error || err.response.data.details || 'No se pudo registrar el ingreso');
      } else if (err.request) {
        setError('No se pudo conectar con el servidor');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
      setUploadingFiles(false);
    }
  };

  const handleAsignacionChange = (asignacion) => {
    setAsignaciones((prev) => {
      const newAsignaciones = { ...prev, [asignacion]: !prev[asignacion] };

      // Si se desmarca usuarios_profit, desmarcar todos los submódulos
      if (asignacion === 'usuarios_profit' && !newAsignaciones.usuarios_profit) {
        newAsignaciones.profit_administrativo = false;
        newAsignaciones.profit_contable = false;
        newAsignaciones.profit_nomina = false;
      }

      // Si se marca algún submódulo, marcar usuarios_profit
      if (['profit_administrativo', 'profit_contable', 'profit_nomina'].includes(asignacion) &&
        newAsignaciones[asignacion]) {
        newAsignaciones.usuarios_profit = true;
      }

      // Si se desmarca correo_electronico, limpiar el campo de correo
      if (asignacion === 'correo_electronico' && !newAsignaciones.correo_electronico) {
        setFormData((prevForm) => ({ ...prevForm, correo: '' }));
      }

      return newAsignaciones;
    });
  };

  const handleHardwareChange = (tipo, valor) => {
    setHardwareRequerido((prev) => ({ ...prev, [tipo]: valor }));
  };

  // Un solo archivo por tipo, imagen o PDF
  const seleccionarArchivo = (setter) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!(file.type.startsWith('image/') || file.type === 'application/pdf')) {
      alert('Solo se permiten archivos de imagen o PDF');
      e.target.value = '';
      return;
    }
    setter([file]);
    e.target.value = '';
  };

  const renderAdjunto = (titulo, inputId, files, onSelect, onRemove) => (
    <div className="adjunto-group">
      <h4>{titulo}</h4>
      <div className="file-upload-container">
        <input
          type="file"
          id={inputId}
          accept="image/*,application/pdf"
          onChange={onSelect}
          style={{ display: 'none' }}
        />
        <label htmlFor={inputId} className="file-upload-button">
          <FaUpload /> Seleccionar {titulo}
        </label>
        {files.length > 0 && (
          <div className="selected-files">
            <ul className="selected-files-list">
              {files.map((file, index) => (
                <li key={index} className="selected-file-item">
                  <span>{file.name}</span>
                  <button
                    type="button"
                    onClick={onRemove}
                    className="remove-file-button"
                    aria-label="Quitar archivo"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );

  const pasos = (
    <div className="vac-pasos" aria-hidden="true">
      <span className={`vac-paso ${paso === 1 ? 'vac-paso--activo' : 'vac-paso--hecho'}`}>
        <span className="vac-paso-num">1</span> Vacante
      </span>
      <span className="vac-pasos-linea" />
      <span className={`vac-paso ${paso === 2 ? 'vac-paso--activo' : ''}`}>
        <span className="vac-paso-num">2</span> Persona
      </span>
    </div>
  );

  const selectorVacante = (
    <VacantePicker
      intro="¿Qué puesto va a ocupar? Todo ingreso cubre una vacante abierta."
      onElegir={elegirVacante}
      onAbriendoVacante={setShowAbrirVacante}
      onError={setError}
    />
  );

  const fichaPuesto = vacante && (
    <div className="vac-ficha-puesto">
      <span className="vac-asiento" />
      <div className="vac-ficha-puesto-cuerpo">
        <div className="vac-ficha-puesto-etiqueta">Cubre la vacante</div>
        <div className="vac-ficha-puesto-titulo">{vacante.titulo}</div>
        <div className="vac-ficha-puesto-ruta">
          {vacante.cargo && vacante.cargo !== vacante.titulo ? `${vacante.cargo} en ` : ''}{rutaPuesto(vacante)}
        </div>
        <div className="vac-ficha-puesto-reporta">
          {vacante.supervisor_nombre
            ? <>Reporta a <strong>{vacante.supervisor_nombre}</strong></>
            : 'Sin supervisor asignado'}
        </div>
      </div>
      {!puestoFijo && (
        <button type="button" className="vac-link-btn" onClick={() => setPaso(1)}>
          Cambiar
        </button>
      )}
    </div>
  );

  const formularioPersona = (
    <form onSubmit={handleSubmit} id="new-employee-form">
      {fichaPuesto}

      {candidatos.length > 0 && (
        <div className="form-modal-section">
          <h3><FaUsers /> ¿Quién ingresa?</h3>
          <p className="text-sm text-gray-600 -mt-3 mb-3">
            Esta vacante tiene candidatos. Si ingresa uno de ellos, sus datos se cargan solos y
            queda como contratado; el resto se descarta al cubrirse la vacante.
          </p>
          <div className="vac-decision" role="radiogroup" aria-label="Quién ingresa">
            {candidatos.map((c) => (
              <label
                key={c.id}
                className={`vac-decision-opcion${quienIngresa === c.id ? ' vac-decision-opcion--activa' : ''}`}
              >
                <input
                  type="radio"
                  name="quien_ingresa"
                  checked={quienIngresa === c.id}
                  onChange={() => elegirQuien(c.id)}
                />
                <span className={`vac-asiento${c.estado === 'Seleccionado' ? ' vac-asiento--cubierta' : ''}`}>
                  {iniciales(c.nombre)}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="vac-decision-titulo block">{c.nombre}</span>
                  <span className="vac-decision-detalle block">
                    {c.estado}{c.cedula ? `, cédula ${c.cedula}` : ''}
                  </span>
                </span>
              </label>
            ))}
            <label className={`vac-decision-opcion${quienIngresa === 'otro' ? ' vac-decision-opcion--activa' : ''}`}>
              <input
                type="radio"
                name="quien_ingresa"
                checked={quienIngresa === 'otro'}
                onChange={() => elegirQuien('otro')}
              />
              <span className="flex-1 min-w-0">
                <span className="vac-decision-titulo block">Otra persona</span>
                <span className="vac-decision-detalle block">No pasó por los candidatos de la vacante</span>
              </span>
            </label>
          </div>
        </div>
      )}

      <div className="form-modal-section">
        <h3><FaUser /> Datos de la persona</h3>
        <div className="form-modal-grid three-cols">
          <div className="form-modal-group">
            <label htmlFor="apellidos">
              <FaUser className="input-icon" /> Apellidos
            </label>
            <input
              type="text"
              id="apellidos"
              name="apellidos"
              value={formData.apellidos}
              onChange={handleInputChange}
              placeholder="Apellidos"
              required
            />
          </div>

          <div className="form-modal-group">
            <label htmlFor="nombres">
              <FaUser className="input-icon" /> Nombres
            </label>
            <input
              type="text"
              id="nombres"
              name="nombres"
              value={formData.nombres}
              onChange={handleInputChange}
              placeholder="Nombres"
              required
            />
          </div>

          <div className="form-modal-group">
            <label htmlFor="cedula">
              <FaIdCard className="input-icon" /> Cédula
            </label>
            <input
              type="text"
              inputMode="numeric"
              id="cedula"
              name="cedula"
              value={formData.cedula}
              onChange={handleInputChange}
              placeholder="12345678"
              required
            />
          </div>

          <div className="form-modal-group">
            <label htmlFor="fecha_nacimiento">
              <FaBirthdayCake className="input-icon" /> Fecha de nacimiento
            </label>
            <input
              type="date"
              id="fecha_nacimiento"
              name="fecha_nacimiento"
              value={formData.fecha_nacimiento}
              onChange={handleInputChange}
              max={new Date().toISOString().slice(0, 10)}
              required
            />
          </div>

          <div className="form-modal-group">
            <label htmlFor="ficha">
              <FaIdCard className="input-icon" /> Ficha
            </label>
            <input
              type="text"
              inputMode="numeric"
              id="ficha"
              name="ficha"
              value={formData.ficha}
              onChange={handleInputChange}
              placeholder="4 dígitos"
              required
              maxLength="4"
            />
          </div>

          <div className="form-modal-group">
            <label htmlFor="fecha_ingreso">
              <FaCalendarAlt className="input-icon" /> Fecha de ingreso
            </label>
            <input
              type="date"
              id="fecha_ingreso"
              name="fecha_ingreso"
              value={formData.fecha_ingreso}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-modal-group">
            <label htmlFor="sede_id">
              <FaBuilding className="input-icon" /> Sede
            </label>
            <select
              id="sede_id"
              name="sede_id"
              value={formData.sede_id}
              onChange={handleInputChange}
              required
            >
              <option value="">Seleccione sede</option>
              {sedes.map((sede) => (
                <option key={sede.id} value={sede.id}>
                  {sede.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="form-modal-group md:col-span-1 lg:col-span-2">
            <label htmlFor="detalles_cargo">
              <FaBriefcase className="input-icon" /> Detalles del cargo
            </label>
            <input
              type="text"
              id="detalles_cargo"
              name="detalles_cargo"
              value={formData.detalles_cargo}
              onChange={handleInputChange}
              placeholder="Opcional, por ejemplo: Centro Occidente"
            />
          </div>
        </div>
      </div>

      {/* Secciones de Software y Hardware lado a lado */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="form-modal-section">
          <h3><FaNetworkWired /> Software y Accesos</h3>
          <div className="space-y-3">
            <div className="asignacion-item">
              <input
                type="checkbox"
                id="usuario_red"
                checked={asignaciones.usuario_red}
                onChange={() => handleAsignacionChange('usuario_red')}
              />
              <label htmlFor="usuario_red"><FaNetworkWired className="inline mr-2 text-gray-400" />Usuario de Red</label>
            </div>
            <div className="asignacion-item">
              <input
                type="checkbox"
                id="correo_electronico"
                checked={asignaciones.correo_electronico}
                onChange={() => handleAsignacionChange('correo_electronico')}
              />
              <label htmlFor="correo_electronico"><FaEnvelope className="inline mr-2 text-gray-400" />Correo Electrónico</label>
            </div>
            <div className="asignacion-item">
              <input
                type="checkbox"
                id="huella_acceso"
                checked={asignaciones.huella_acceso}
                onChange={() => handleAsignacionChange('huella_acceso')}
              />
              <label htmlFor="huella_acceso"><FaFingerprint className="inline mr-2 text-gray-400" />Acceso Biométrico</label>
            </div>

            <div className="asignacion-group">
              <div className="asignacion-item main">
                <input
                  type="checkbox"
                  id="usuarios_profit"
                  checked={asignaciones.usuarios_profit}
                  onChange={() => handleAsignacionChange('usuarios_profit')}
                />
                <label htmlFor="usuarios_profit"><FaCalculator className="inline mr-2 text-gray-400" />Usuarios de Profit</label>
              </div>

              {asignaciones.usuarios_profit && (
                <div className="asignacion-subitems">
                  {[
                    ['profit_administrativo', 'Administrativo'],
                    ['profit_contable', 'Contable'],
                    ['profit_nomina', 'Nómina SPI'],
                  ].map(([clave, etiqueta]) => (
                    <div className="asignacion-item sub" key={clave}>
                      <input
                        type="checkbox"
                        id={clave}
                        checked={asignaciones[clave]}
                        onChange={() => handleAsignacionChange(clave)}
                      />
                      <label htmlFor={clave}>{etiqueta}</label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {[
              ['centro_sura', 'Usuario Centro Sura'],
              ['cima', 'Usuario Cima'],
              ['soluciones_ti', 'Usuario Soluciones TI'],
            ].map(([clave, etiqueta]) => (
              <div className="asignacion-item" key={clave}>
                <input
                  type="checkbox"
                  id={clave}
                  checked={asignaciones[clave]}
                  onChange={() => handleAsignacionChange(clave)}
                />
                <label htmlFor={clave}><FaUser className="inline mr-2 text-gray-400" />{etiqueta}</label>
              </div>
            ))}
          </div>
        </div>

        <div className="form-modal-section">
          <h3><FaDesktop /> Hardware Requerido</h3>
          <div className="space-y-4">
            <div className="hardware-group">
              <h4><FaLaptop className="inline mr-2" />Tipo de Equipo</h4>
              <div className="radio-group">
                {[
                  ['laptop', 'Laptop'],
                  ['aio', 'All-in-One'],
                  ['ninguno', 'Ninguno'],
                ].map(([valor, etiqueta]) => (
                  <div className="radio-item" key={valor}>
                    <input
                      type="radio"
                      id={valor}
                      name="tipo_equipo"
                      value={valor}
                      checked={hardwareRequerido.tipo_equipo === valor}
                      onChange={(e) => handleHardwareChange('tipo_equipo', e.target.value)}
                    />
                    <label htmlFor={valor}>{etiqueta}</label>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-4">
              <div className="hardware-group flex-1">
                <h4><FaMobileAlt className="inline mr-2" />Teléfono</h4>
                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="telefono"
                    checked={hardwareRequerido.telefono}
                    onChange={(e) => handleHardwareChange('telefono', e.target.checked)}
                  />
                  <label htmlFor="telefono">Requiere smartphone</label>
                </div>
              </div>

              <div className="hardware-group flex-1">
                <h4><FaTabletAlt className="inline mr-2" />Tablet</h4>
                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="tablet"
                    checked={hardwareRequerido.tablet}
                    onChange={(e) => handleHardwareChange('tablet', e.target.checked)}
                  />
                  <label htmlFor="tablet">Requiere tablet</label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="form-modal-section">
        <h3><FaPaperclip /> Documentos Adjuntos</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderAdjunto('RIF', 'file-upload-rif', selectedFilesRif,
            seleccionarArchivo(setSelectedFilesRif), () => setSelectedFilesRif([]))}
          {renderAdjunto('Cédula', 'file-upload-cedula', selectedFilesCedula,
            seleccionarArchivo(setSelectedFilesCedula), () => setSelectedFilesCedula([]))}
        </div>

        {uploadingFiles && (
          <div className="upload-progress mt-4">
            <FaSpinner className="spinning" />
            Subiendo archivos... {uploadProgress}%
          </div>
        )}
      </div>

      <div className="form-modal-section">
        <h3><FaBell /> Notificaciones</h3>
        <div className="checkbox-item">
          <input
            type="checkbox"
            id="enviar_correo"
            checked={enviarCorreo}
            onChange={(e) => setEnviarCorreo(e.target.checked)}
          />
          <label htmlFor="enviar_correo">Enviar correo según la configuración de destinatarios para Ficha TI</label>
        </div>
      </div>
    </form>
  );

  return (
    <Modal isOpen={true} onClose={onClose} size="xl" closeOnEscape={!showAbrirVacante}>
      <Modal.Header onClose={onClose}>Registrar ingreso</Modal.Header>
      <Modal.Body>
        {!puestoFijo && pasos}

        {error && (
          <div className="form-modal-error mb-4">
            <FaExclamationTriangle />
            {error}
          </div>
        )}

        {paso === 1 ? selectorVacante : (
          vacante ? formularioPersona : (
            !error && <div className="vac-vacio"><p><FaSpinner className="animate-spin inline mr-2" />Cargando vacante...</p></div>
          )
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Cancelar
        </button>
        {paso === 2 && (
          <button
            type="submit"
            form="new-employee-form"
            className="btn btn-primary"
            disabled={loading || !vacante || !ESTADOS_VIGENTES.includes(vacante.estado)}
          >
            {loading ? <FaSpinner className="animate-spin" /> : 'Registrar ingreso'}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default NewEmployeeModal;
